<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { CirclePlus, Delete, Edit, Plus } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import uploadFile from '@/components/publicComponent/uploadFile.vue'
import area from '@/common/json/area.json'
import { handleTreeData } from '@/common/js/common.js'

const store = useStore()
const route = useRoute()
const router = useRouter()

const enterpriseFormRef = ref()
const invoiceFormRef = ref()
const addressFormRef = ref()
const emit = defineEmits(['switch-tab'])

const enterpriseDetail = ref({})
const enterpriseFileList = ref([])
const sealFileList = ref([])
const paymentFileList = ref([])
const companyTypeOptions = ref([])
const companyTypeDisplayList = ref([])
const addressList = ref([])
const invoiceData = ref({})
const userInfo = ref({})

const isEnterpriseEdit = ref(false)
const isSealEdit = ref(false)
const isPaymentEdit = ref(false)
const isInvoiceEdit = ref(false)

const addressDialogVisible = ref(false)
const addressDialogMode = ref('add')
const activeAddressId = ref('')

const areaOptions = handleTreeData(area, 'name', 'name')

const enterpriseForm = reactive({
  enterpriseId: '',
  enterpriseName: '',
  enterpriseType: null,
  areas: [],
  address: '',
  enterpriseLicense: []
})

const sealForm = reactive({
  ids: []
})

const paymentForm = reactive({
  ids: []
})

const invoiceForm = reactive({
  id: '',
  invoiceHead: '',
  invoicePhone: '',
  bankNumber: '',
  address: '',
  bankName: '',
  taxpayerCode: '',
  bankAccount: ''
})

const addressForm = reactive({
  value: [],
  address: '',
  consigneeName: '',
  consigneePhone: '',
  remarks: '',
  defaultFlag: '0'
})

const enterpriseRules = {
  enterpriseLicense: [{ required: true, message: '请上传营业执照', trigger: 'change' }]
}

const invoiceRules = {
  invoiceHead: [{ required: true, message: '请输入公司名称', trigger: 'blur' }],
  invoicePhone: [{ required: true, message: '请输入开票电话', trigger: 'blur' }],
  address: [{ required: true, message: '请输入公司地址', trigger: 'blur' }],
  bankName: [{ required: true, message: '请输入开户行', trigger: 'blur' }],
  taxpayerCode: [{ required: true, message: '请输入公司税号', trigger: 'blur' }],
  bankAccount: [{ required: true, message: '请输入银行账户', trigger: 'blur' }]
}

const addressRules = {
  value: [{ required: true, message: '请选择所属地区', trigger: 'change' }],
  address: [{ required: true, message: '请输入详细地址', trigger: 'blur' }],
  consigneeName: [{ required: true, message: '请输入收货人', trigger: 'blur' }],
  consigneePhone: [{ required: true, message: '请输入收货人电话', trigger: 'blur' }]
}

const canEditEnterprise = computed(
  () => enterpriseDetail.value.contactPersonFlag !== '1' && enterpriseDetail.value.isEdit !== '1'
)
const canEditSeal = computed(() => enterpriseDetail.value.contactPersonFlag === '1')
const enterprisePermissionText = computed(() => {
  if (isEnterpriseEdit.value) {
    return '当前正在编辑企业基础资料，营业执照可随本次修改一并上传保存。'
  }
  if (enterpriseDetail.value.contactPersonFlag === '1') {
    return '营业执照不由主账号单独维护，请使用可编辑企业基础资料的账号处理。'
  }
  if (enterpriseDetail.value.isEdit === '1') {
    return '企业基础资料已锁定，营业执照当前不可再修改。'
  }
  return '营业执照跟随企业基础资料一起修改，点击“编辑”后可重新上传。'
})
const sealPermissionText = computed(() =>
  canEditSeal.value ? '合同章仅主账号可维护，点击“编辑”后可上传并保存。' : '合同章仅主账号可修改，当前账号只有查看权限。'
)
const paymentPermissionText = computed(() =>
  canEditSeal.value ? '收款码仅主账号可维护，点击“编辑”后可上传并保存。' : '收款码仅主账号可修改，当前账号只有查看权限。'
)

const licenseImage = computed(() => enterpriseFileList.value[0]?.url || '')
const sealImage = computed(() => sealFileList.value[0]?.url || '')
const paymentImage = computed(() => paymentFileList.value[0]?.url || '')

const displayAddressList = computed(() =>
  addressList.value.map(item => ({
    ...item,
    fullAddress: [item.province, item.city, item.district, item.address].filter(Boolean).join(' '),
    zipCode: item.zipCode || item.postCode || item.postalCode || '--'
  }))
)

const receiverInfo = computed(() => {
  const current =
    addressList.value.find(item => item.defaultFlag === '1') || addressList.value[0] || {}
  return {
    name: current.consigneeName || '--',
    email: current.email || '--',
    address: [current.province, current.city, current.district, current.address].filter(Boolean).join(' ') || '--',
    phone: current.consigneePhone || '--',
    zipCode: current.zipCode || current.postCode || current.postalCode || '--'
  }
})

const parseFileList = value => {
  if (!value) {
    return []
  }
  if (Array.isArray(value)) {
    return value
  }
  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value)
      return Array.isArray(parsedValue) ? parsedValue : []
    } catch (error) {
      return []
    }
  }
  return []
}

const cloneData = value => JSON.parse(JSON.stringify(value))

const findTypePath = (nodes, targetId, path = []) => {
  for (const node of nodes || []) {
    const currentPath = [...path, node.categoryName]
    if (node.id === targetId) {
      return currentPath
    }
    if (node.children?.length) {
      const childPath = findTypePath(node.children, targetId, currentPath)
      if (childPath.length) {
        return childPath
      }
    }
  }
  return []
}

const parseEnterpriseTypeIds = value => {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean)
  }
  if (value === undefined || value === null || value === '') {
    return []
  }
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

const getExistingFileId = files => files[0]?.fileId || files[0]?.id || files[0]?.uid || null

const getUploadValue = (newIds, existingFiles) =>
  newIds.length ? newIds[0] : getExistingFileId(existingFiles)

const getExistingFileIds = files =>
  (files || []).map(item => item.fileId || item.id || item.uid).filter(Boolean)

const getAttachmentIdsString = value => getExistingFileIds(parseFileList(value)).join(',')

const buildEnterpriseSubmitPayload = () => {
  const payload = {
    ...cloneData(enterpriseDetail.value),
    enterpriseId: enterpriseDetail.value.id,
    enterpriseType: enterpriseForm.enterpriseType,
    address: enterpriseForm.address,
    areas: cloneData(enterpriseForm.areas),
    enterpriseLicense: cloneData(enterpriseForm.enterpriseLicense),
    ids: cloneData(sealForm.ids),
    collectionQrCodeIds: cloneData(paymentForm.ids)
  }
  payload.enterpriseLicense = payload.enterpriseLicense[0]
  payload.province = payload.areas[0]
  payload.city = payload.areas[1]
  payload.district = payload.areas[2]
  payload.sealFlies = payload.ids.length ? payload.ids[0] : null
  payload.collectionQrCode = payload.collectionQrCodeIds.length ? payload.collectionQrCodeIds[0] : null
  payload.enterpriseQualification = getAttachmentIdsString(enterpriseDetail.value.enterpriseQualification)
  payload.officeWorkshopEnvironment = getAttachmentIdsString(enterpriseDetail.value.officeWorkshopEnvironment)
  delete payload.areas
  delete payload.collectionQrCodeIds
  return payload
}

const resetEnterpriseForm = () => {
  const detail = enterpriseDetail.value
  enterpriseForm.enterpriseId = detail.enterpriseCode || detail.enterpriseId || detail.id || ''
  enterpriseForm.enterpriseName = detail.enterpriseName || ''
  enterpriseForm.enterpriseType = detail.enterpriseType || null
  enterpriseForm.areas = detail.province ? [detail.province, detail.city, detail.district] : []
  enterpriseForm.address = detail.address || ''
  enterpriseFileList.value = parseFileList(detail.enterpriseLicense)
  enterpriseForm.enterpriseLicense = getExistingFileIds(enterpriseFileList.value)
}

const resetSealForm = () => {
  sealFileList.value = cloneData(parseFileList(enterpriseDetail.value.sealFlies))
  sealForm.ids = getExistingFileIds(sealFileList.value)
}

const resetPaymentForm = () => {
  paymentFileList.value = cloneData(parseFileList(enterpriseDetail.value.collectionQrCode))
  paymentForm.ids = getExistingFileIds(paymentFileList.value)
}

const resetInvoiceForm = () => {
  invoiceForm.id = invoiceData.value.id || ''
  invoiceForm.invoiceHead = invoiceData.value.invoiceHead || ''
  invoiceForm.invoicePhone = invoiceData.value.invoicePhone || ''
  invoiceForm.bankNumber = invoiceData.value.bankNumber || ''
  invoiceForm.address = invoiceData.value.address || ''
  invoiceForm.bankName = invoiceData.value.bankName || ''
  invoiceForm.taxpayerCode = invoiceData.value.taxpayerCode || ''
  invoiceForm.bankAccount = invoiceData.value.bankAccount || ''
}

const resetAddressForm = () => {
  addressForm.value = []
  addressForm.address = ''
  addressForm.consigneeName = ''
  addressForm.consigneePhone = ''
  addressForm.remarks = ''
  addressForm.defaultFlag = '0'
}

const syncCompanyTypeDisplay = () => {
  const typeIds = parseEnterpriseTypeIds(enterpriseDetail.value.enterpriseType)
  companyTypeDisplayList.value = typeIds
    .map(typeId => {
      const path = findTypePath(companyTypeOptions.value, typeId)
      if (!path.length) {
        return null
      }
      return {
        id: typeId,
        name: path.join(' / ')
      }
    })
    .filter(Boolean)
}

const loadCompanyTypes = async () => {
  const res = await store.dispatch('myMessageModule/enterpriseTreeList', {})
  const { code, data = [] } = res?.data || {}
  if (code === '0') {
    companyTypeOptions.value = data
    syncCompanyTypeDisplay()
  }
}

const loadEnterpriseInfo = async enterpriseId => {
  const res = await store.dispatch('myMessageModule/getEnterprise', enterpriseId)
  const { code, data = {} } = res?.data || {}
  if (code !== '0') {
    return
  }

  enterpriseDetail.value = data
  resetEnterpriseForm()
  resetSealForm()
  resetPaymentForm()
  syncCompanyTypeDisplay()
}

const loadInvoiceInfo = async () => {
  const res = await store.dispatch('myMessageModule/userGetUserInvoice', {})
  const { code, data = {} } = res?.data || {}
  if (code !== '0') {
    return
  }

  invoiceData.value = data
  resetInvoiceForm()
}

const loadAddressInfo = async () => {
  const res = await store.dispatch('myMessageModule/userConsigneeList')
  const { code, data = [] } = res?.data || {}
  if (code === '0') {
    addressList.value = Array.isArray(data) ? data : []
  }
}

const init = async () => {
  const userRes = await store.dispatch('myMessageModule/getUser')
  const { code, data = {} } = userRes?.data || {}
  if (code !== '0') {
    return
  }

  userInfo.value = data
  await Promise.allSettled([
    loadCompanyTypes(),
    data.enterpriseId ? loadEnterpriseInfo(data.enterpriseId) : Promise.resolve(),
    loadInvoiceInfo(),
    loadAddressInfo()
  ])
}

const startEnterpriseEdit = () => {
  isEnterpriseEdit.value = true
  resetEnterpriseForm()
}

const cancelEnterpriseEdit = () => {
  isEnterpriseEdit.value = false
  resetEnterpriseForm()
}

const saveEnterprise = async () => {
  const valid = await enterpriseFormRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }

  ElMessageBox.confirm('企业基础资料只能修改一次，是否确认修改？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const payload = buildEnterpriseSubmitPayload()
      const res = await store.dispatch('myMessageModule/editEnterprise', payload)
      if (res?.data?.code === '0') {
        ElMessage.success('修改成功')
        isEnterpriseEdit.value = false
        await loadEnterpriseInfo(userInfo.value.enterpriseId)
      }
    })
    .catch(() => {})
}

const startSealEdit = () => {
  isSealEdit.value = true
  resetSealForm()
}

const cancelSealEdit = () => {
  isSealEdit.value = false
  resetSealForm()
}

const saveSeal = async () => {
  const payload = buildEnterpriseSubmitPayload()
  const res = await store.dispatch('myMessageModule/editEnterprise', payload)
  if (res?.data?.code === '0') {
    ElMessage.success('修改成功')
    isSealEdit.value = false
    await loadEnterpriseInfo(userInfo.value.enterpriseId)
  }
}

const deleteSeal = () => {
  ElMessageBox.confirm('是否删除合同章？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const payload = buildEnterpriseSubmitPayload()
      payload.ids = []
      payload.sealFlies = null
      const res = await store.dispatch('myMessageModule/editEnterprise', payload)
      if (res?.data?.code === '0') {
        ElMessage.success('删除成功')
        await loadEnterpriseInfo(userInfo.value.enterpriseId)
      }
    })
    .catch(() => {})
}

const startPaymentEdit = () => {
  isPaymentEdit.value = true
  resetPaymentForm()
}

const cancelPaymentEdit = () => {
  isPaymentEdit.value = false
  resetPaymentForm()
}

const savePayment = async () => {
  const payload = buildEnterpriseSubmitPayload()
  const res = await store.dispatch('myMessageModule/editEnterprise', payload)
  if (res?.data?.code === '0') {
    ElMessage.success('修改成功')
    isPaymentEdit.value = false
    await loadEnterpriseInfo(userInfo.value.enterpriseId)
    const documentId = route.query.enableCollectionQrCodeForOrder
    if (documentId && paymentForm.ids.length) {
      const statusRes = await store.dispatch('mysaleMudule/purchaseUpdateCollectionQrCodeStatus', {
        documentId,
        collectionQrCodeEnabled: '1'
      })
      if (statusRes?.data?.code === '0') {
        ElMessage.success('收款码已自动启用')
        router.push({ path: '/mysale/xinOrderDetails', query: { id: documentId } })
      }
    }
  }
}

const deletePayment = () => {
  ElMessageBox.confirm('是否删除收款码？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const payload = buildEnterpriseSubmitPayload()
      payload.collectionQrCode = null
      payload.collectionQrCodeIds = []
      const res = await store.dispatch('myMessageModule/editEnterprise', payload)
      if (res?.data?.code === '0') {
        ElMessage.success('删除成功')
        await loadEnterpriseInfo(userInfo.value.enterpriseId)
      }
    })
    .catch(() => {})
}

const startInvoiceEdit = () => {
  isInvoiceEdit.value = true
  resetInvoiceForm()
}

const cancelInvoiceEdit = () => {
  isInvoiceEdit.value = false
  resetInvoiceForm()
}

const saveInvoice = async () => {
  const valid = await invoiceFormRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }

  const res = await store.dispatch('myMessageModule/userInvoiceEdit', cloneData(invoiceForm))
  if (res?.data?.code === '0') {
    ElMessage.success('编辑成功')
    isInvoiceEdit.value = false
    await loadInvoiceInfo()
  }
}

const openAddressDialog = async (mode, row) => {
  addressDialogMode.value = mode
  activeAddressId.value = row?.id || ''
  resetAddressForm()

  if (mode === 'edit' || mode === 'view') {
    const res = await store.dispatch('myMessageModule/getUserConsignee', row.id)
    const { code, data = {} } = res?.data || {}
    if (code === '0') {
      addressForm.value = [data.province, data.city, data.district]
      addressForm.address = data.address || ''
      addressForm.consigneeName = data.consigneeName || ''
      addressForm.consigneePhone = data.consigneePhone || ''
      addressForm.remarks = data.remarks || ''
      addressForm.defaultFlag = data.defaultFlag || '0'
    }
  }

  addressDialogVisible.value = true
}

const closeAddressDialog = () => {
  addressDialogVisible.value = false
  activeAddressId.value = ''
  resetAddressForm()
}

const saveAddress = async () => {
  if (addressDialogMode.value === 'view') {
    closeAddressDialog()
    return
  }

  const valid = await addressFormRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }

  const payload = {
    address: addressForm.address,
    consigneeName: addressForm.consigneeName,
    consigneePhone: addressForm.consigneePhone,
    remarks: addressForm.remarks,
    defaultFlag: addressForm.defaultFlag,
    province: addressForm.value[0],
    city: addressForm.value[1],
    district: addressForm.value[2]
  }

  const action =
    addressDialogMode.value === 'add'
      ? 'myMessageModule/addUserConsignee'
      : 'myMessageModule/editUserConsignee'
  const res = await store.dispatch(
    action,
    addressDialogMode.value === 'add' ? payload : { ...payload, id: activeAddressId.value }
  )
  if (res?.data?.code === '0') {
    ElMessage.success(addressDialogMode.value === 'add' ? '新增成功' : '修改成功')
    closeAddressDialog()
    await loadAddressInfo()
  }
}

const deleteAddress = row => {
  ElMessageBox.confirm('是否删除该收货地址信息？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const res = await store.dispatch('myMessageModule/deleteUserConsignee', row.id)
      if (res?.data?.code === '0') {
        ElMessage.success('删除成功')
        await loadAddressInfo()
      }
    })
    .catch(() => {})
}

onMounted(async () => {
  await init()
  if (route.query.enableCollectionQrCodeForOrder) {
    startPaymentEdit()
  }
})
</script>

<template>
  <div class="base-info">
    <div class="section-card license">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">营业执照</span>
        </div>
      </div>
      <el-form
        ref="enterpriseFormRef"
        :model="enterpriseForm"
        :rules="isEnterpriseEdit ? enterpriseRules : {}"
        label-width="120px"
        class="license-form"
        label-position="left"
      >
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="企业盘雷ID：">
              <el-input v-model="enterpriseForm.enterpriseId" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="企业名称：">
              <el-input v-model="enterpriseForm.enterpriseName" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="营业执照：" prop="enterpriseLicense" class="upload">
          <div class="license-upload-area">
            <div class="license-preview">
              <img v-if="licenseImage" :src="licenseImage" alt="营业执照" class="preview-img" />
              <div v-else class="empty-image empty-image--license">暂无图片</div>
            </div>
            <div class="media-side">
              <upload-file
                v-if="isEnterpriseEdit"
                v-model="enterpriseForm.enterpriseLicense"
                is-type="add"
                list-type="picture-card"
                :limit="1"
                :list="enterpriseFileList"
                only-image
                class="license-upload"
              />
              <div v-else class="permission-tip media-tip">{{ enterprisePermissionText }}</div>
            </div>
          </div>
        </el-form-item>
        <div v-if="canEditEnterprise" class="action-row">
          <template v-if="!isEnterpriseEdit">
            <el-button type="primary" size="small" @click="startEnterpriseEdit">编辑</el-button>
          </template>
          <template v-else>
            <el-button size="small" @click="cancelEnterpriseEdit">取消</el-button>
            <el-button type="primary" size="small" @click="saveEnterprise">确认</el-button>
          </template>
        </div>
      </el-form>
    </div>

    <div class="section-card">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">合同章</span>
        </div>
        <div v-if="canEditSeal" class="btn-group">
          <template v-if="!isSealEdit">
            <el-button link size="small" @click="startSealEdit">
              <el-icon><Edit /></el-icon>
              <span>编辑</span>
            </el-button>
            <el-button link size="small" @click="deleteSeal">
              <el-icon><Delete /></el-icon>
              <span>删除</span>
            </el-button>
          </template>
          <template v-else>
            <el-button link size="small" @click="cancelSealEdit">取消</el-button>
            <el-button link size="small" @click="saveSeal">保存</el-button>
          </template>
        </div>
      </div>
      <el-form label-width="80px">
        <el-form-item label="公章：">
          <div class="media-content">
            <div v-if="!isSealEdit" class="image-preview">
              <img v-if="sealImage" :src="sealImage" alt="合同章" class="square-img" />
              <div v-else class="empty-image empty-image--square">暂无图片</div>
            </div>
            <upload-file
              v-else
              v-model="sealForm.ids"
              is-type="add"
              list-type="picture-card"
              :limit="1"
              :list="sealFileList"
              only-image
              :allowed-mime-types="['image/png']"
              invalid-file-type-message="仅支持 PNG 格式图片，请重新选择。"
            />
            <div v-if="isSealEdit" class="upload-format-tip">请上传 PNG 格式图片</div>
            <div v-if="!isSealEdit" class="permission-tip media-tip">{{ sealPermissionText }}</div>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <div class="section-card">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">收款码</span>
        </div>
        <div v-if="canEditSeal" class="btn-group">
          <template v-if="!isPaymentEdit">
            <el-button link size="small" @click="startPaymentEdit">
              <el-icon><Edit /></el-icon>
              <span>编辑</span>
            </el-button>
            <el-button link size="small" @click="deletePayment">
              <el-icon><Delete /></el-icon>
              <span>删除</span>
            </el-button>
          </template>
          <template v-else>
            <el-button link size="small" @click="cancelPaymentEdit">取消</el-button>
            <el-button link size="small" @click="savePayment">保存</el-button>
          </template>
        </div>
      </div>
      <el-form label-width="80px">
        <el-form-item label="收款码：">
          <div class="media-content">
            <div v-if="!isPaymentEdit" class="image-preview">
              <img v-if="paymentImage" :src="paymentImage" alt="收款码" class="square-img" />
              <div v-else class="empty-image empty-image--square">暂无图片</div>
            </div>
            <upload-file
              v-else
              v-model="paymentForm.ids"
              is-type="add"
              list-type="picture-card"
              :limit="1"
              :list="paymentFileList"
              only-image
            />
            <div v-if="!isPaymentEdit" class="permission-tip media-tip">{{ paymentPermissionText }}</div>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <div class="section-card">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">公司类型</span>
        </div>
        <div class="btn">
          <el-button link size="small" @click="emit('switch-tab', '2')">
            <span>去设置</span>
          </el-button>
        </div>
      </div>
      <div class="row">
        <span class="label">公司类型：</span>
        <template v-if="companyTypeDisplayList.length">
          <template v-for="(item, index) in companyTypeDisplayList" :key="item.id">
            <span class="text">{{ item.name }}</span>
            <div v-if="index < companyTypeDisplayList.length - 1" class="divider"></div>
          </template>
        </template>
        <span v-else class="text">--</span>
      </div>
    </div>

    <div class="section-card invoice-info">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">开票信息</span>
        </div>
        <div class="btn">
          <template v-if="!isInvoiceEdit">
            <el-button link size="small" @click="startInvoiceEdit">
              <el-icon><Edit /></el-icon>
              <span>编辑</span>
            </el-button>
          </template>
          <template v-else>
            <el-button link size="small" @click="cancelInvoiceEdit">取消</el-button>
            <el-button link size="small" @click="saveInvoice">保存</el-button>
          </template>
        </div>
      </div>

      <el-form ref="invoiceFormRef" :model="invoiceForm" :rules="invoiceRules" label-width="0">
        <div class="section">
          <h3 class="sub-title">开票信息</h3>
          <el-row :gutter="20" class="info-row">
            <el-col :span="6">
              <div class="info-item">
                <span class="label">公司名称：</span>
                <el-form-item prop="invoiceHead" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.invoiceHead" />
                  <span v-else class="value">{{ invoiceForm.invoiceHead || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="info-item">
                <span class="label">银行账户：</span>
                <el-form-item prop="bankAccount" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.bankAccount" />
                  <span v-else class="value">{{ invoiceForm.bankAccount || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="info-item">
                <span class="label">公司地址：</span>
                <el-form-item prop="address" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.address" />
                  <span v-else class="value">{{ invoiceForm.address || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="info-item">
                <span class="label">公司税号：</span>
                <el-form-item prop="taxpayerCode" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.taxpayerCode" />
                  <span v-else class="value">{{ invoiceForm.taxpayerCode || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20" class="info-row">
            <el-col :span="6">
              <div class="info-item">
                <span class="label">行号：</span>
                <el-form-item prop="bankNumber" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.bankNumber" />
                  <span v-else class="value">{{ invoiceForm.bankNumber || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="info-item">
                <span class="label">开户行：</span>
                <el-form-item prop="bankName" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.bankName" />
                  <span v-else class="value">{{ invoiceForm.bankName || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="info-item">
                <span class="label">开票电话：</span>
                <el-form-item prop="invoicePhone" class="inline-form-item">
                  <el-input v-if="isInvoiceEdit" v-model="invoiceForm.invoicePhone" />
                  <span v-else class="value">{{ invoiceForm.invoicePhone || '--' }}</span>
                </el-form-item>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-form>

      <div class="section">
        <h3 class="sub-title">收货人信息：</h3>
        <el-row :gutter="20" class="info-row">
          <el-col :span="6">
            <div class="info-item">
              <span class="label">姓名：</span>
              <span class="value">{{ receiverInfo.name }}</span>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <span class="label">邮箱：</span>
              <span class="value">{{ receiverInfo.email }}</span>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <span class="label">手机号码：</span>
              <span class="value">{{ receiverInfo.phone }}</span>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <span class="label">邮编：</span>
              <span class="value">{{ receiverInfo.zipCode }}</span>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="20" class="info-row">
          <el-col :span="24">
            <div class="info-item">
              <span class="label">收货地址：</span>
              <span class="value">{{ receiverInfo.address }}</span>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <div class="section-card address">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">收货地址</span>
        </div>
        <div class="btn">
          <el-button link size="small" @click="emit('switch-tab', '3')">
            <el-icon><CirclePlus /></el-icon>
            <span>新增</span>
          </el-button>
        </div>
      </div>
      <div class="address-list">
        <template v-if="displayAddressList.length">
          <div v-for="item in displayAddressList" :key="item.id" class="address-item">
            <div class="address-header">
              <div class="address-title">
                <span class="name">{{ item.consigneeName || '--' }}</span>
                <span v-if="item.defaultFlag === '1'" class="default-tag">默认地址</span>
              </div>
              <div class="address-actions">
                <el-button type="text" class="address-action-btn" @click="openAddressDialog('edit', item)">编辑</el-button>
                <el-button type="text" class="address-action-btn" @click="openAddressDialog('view', item)">查看</el-button>
                <el-button type="text" class="address-action-btn address-action-btn--danger" @click="deleteAddress(item)">
                  删除
                </el-button>
              </div>
            </div>
            <div class="address-content">
              <div class="address-row">
                <div class="address-info-item address-info-item--name">
                  <span class="address-label">收货人：</span>
                  <span class="address-text">{{ item.consigneeName || '--' }}</span>
                </div>
                <div class="address-info-item address-info-item--address">
                  <span class="address-label">地址：</span>
                  <span class="address-text address-text--wide">{{ item.fullAddress || '--' }}</span>
                </div>
              </div>
              <div class="address-row">
                <div class="address-info-item address-info-item--phone">
                  <span class="address-label">手机：</span>
                  <span class="address-text">{{ item.consigneePhone || '--' }}</span>
                </div>
                <div class="address-info-item address-info-item--remark">
                  <span class="address-label">备注：</span>
                  <span class="address-text address-text--wide">{{ item.remarks || '--' }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
        <el-empty v-else description="暂无收货地址" />
      </div>
    </div>

    <el-dialog
      v-model="addressDialogVisible"
      width="600px"
      destroy-on-close
      :title="addressDialogMode === 'add' ? '新增收货地址' : addressDialogMode === 'edit' ? '编辑收货地址' : '查看收货地址'"
      @close="closeAddressDialog"
    >
      <el-form ref="addressFormRef" :model="addressForm" :rules="addressRules" label-width="100px">
        <el-form-item label="所属地区：" prop="value">
          <el-cascader
            v-model="addressForm.value"
            :options="areaOptions"
            :props="{ expandTrigger: 'hover' }"
            style="width: 100%"
            :disabled="addressDialogMode === 'view'"
          />
        </el-form-item>
        <el-form-item label="详细地址：" prop="address">
          <el-input v-model="addressForm.address" :disabled="addressDialogMode === 'view'" />
        </el-form-item>
        <el-form-item label="收货人：" prop="consigneeName">
          <el-input v-model="addressForm.consigneeName" :disabled="addressDialogMode === 'view'" />
        </el-form-item>
        <el-form-item label="收货人电话：" prop="consigneePhone">
          <el-input v-model="addressForm.consigneePhone" :disabled="addressDialogMode === 'view'" />
        </el-form-item>
        <el-form-item label="备注：">
          <el-input v-model="addressForm.remarks" type="textarea" :rows="4" :disabled="addressDialogMode === 'view'" />
        </el-form-item>
        <el-form-item label="默认地址：">
          <el-radio-group v-model="addressForm.defaultFlag" :disabled="addressDialogMode === 'view'">
            <el-radio label="0">否</el-radio>
            <el-radio label="1">是</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeAddressDialog">取消</el-button>
          <el-button type="primary" @click="saveAddress">
            {{ addressDialogMode === 'view' ? '关闭' : '确认' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="less" scoped>
.base-info {
  background: #ffffff;
  height: 100%;
  box-sizing: border-box;
}

.section-card {
  padding: 24px 40px;
  background: #ffffff;
}

.license {
  margin-top: 35px;
}

.license-form {
  max-width: 980px;
  padding-left: 28px;
}

.upload {
  :deep(.el-form-item__content) {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-line {
  width: 2px;
  height: 18px;
  background-color: #48a09c;
  border-radius: 2px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #333333;
  line-height: 20px;
}

.btn,
.btn-group {
  display: flex;
  margin-left: 40px;
  align-items: center;

  :deep(.el-button) {
    color: #48a09c;
    border: none !important;
    outline: none;
  }
}

.license-upload-area {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  min-height: 132px;
}

.media-side {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  min-height: 132px;
}

.media-content {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  min-width: 0;
  min-height: 180px;
}

.license-preview,
.image-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: #f8fbfb;
  border: 1px solid #e5eceb;
}

.license-preview {
  width: 130px;
  height: 130px;
}

.image-preview {
  width: 130px;
  height: 130px;
}

.preview-img,
.square-img {
  width: 100%;
  height: 100%;
}

.preview-img {
  object-fit: cover;
}

.square-img {
  object-fit: contain;
}

.license-upload {
  width: 130px;
  min-height: 130px;
}

.empty-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #8a949e;
  font-size: 14px;
  background: linear-gradient(180deg, #fafcfc 0%, #f3f7f7 100%);
}

.permission-tip {
  flex: 1;
  max-width: none;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: #666666;
  white-space: nowrap;
}

.media-tip {
  align-self: auto;
  padding-top: 0;
}

.upload-format-tip {
  margin-top: 8px;
  font-size: 13px;
  line-height: 20px;
  color: #e67e22;
}

.row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eeeeee;
}

.label {
  margin-right: 8px;
  color: rgba(51, 51, 51, 0.6);
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
}

.text,
.value {
  color: #333333;
  font-size: 14px;
  line-height: 20px;
}

.divider {
  width: 1px;
  height: 24px;
  background: #dddddd;
  margin: 0 8px;
}

.section {
  padding: 10px 20px;
}

.sub-title {
  margin: 18px 0;
  font-size: 16px;
  color: #333333;
}

.info-row {
  margin-bottom: 10px;
}

.info-item {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.inline-form-item {
  margin-bottom: 0;
  flex: 1;

  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}

.address {
  padding-bottom: 28px;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-item {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px 20px;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.address-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.name {
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.default-tag {
  border: 1px solid #f56c6c;
  color: #f56c6c;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
}

.address-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.address-action-btn {
  font-size: 16px;
  color: #409eff;
  padding: 0;
}

.address-action-btn--danger {
  color: #f56c6c;
}

.address-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-row {
  display: flex;
  align-items: center;
  gap: 32px;
}

.address-info-item--name,
.address-info-item--phone {
  width: 280px;
  flex: 0 0 280px;
}

.address-info-item--address,
.address-info-item--remark {
  flex: 1;
  min-width: 0;
}

.address-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.address-label {
  font-size: 16px;
  color: #666;
  white-space: nowrap;
}

.address-text {
  display: inline-block;
  min-width: 220px;
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  word-break: break-all;
}

.address-text--wide {
  min-width: 320px;
}

.action-row {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}

:deep(.el-button--link) {
  font-size: 16px;
  padding: 0;
}

@media (max-width: 768px) {
  .section-card {
    padding: 20px 16px;
  }

  .license-form {
    padding-left: 0;
  }

  .license-upload-area,
  .media-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    min-height: 0;
  }

  .media-side {
    width: 100%;
    min-height: 0;
  }

  .permission-tip {
    white-space: normal;
  }

  .media-tip {
    align-self: flex-start;
    padding-top: 0;
  }

  .address {
    padding: 20px 16px 28px;
  }

  .address-header {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .address-item {
    padding: 16px;
  }

  .address-content {
    align-items: flex-start;
    gap: 14px;
  }

  .address-row {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .address-info-item {
    width: 100%;
    flex: none;
    align-items: flex-start;
    flex-direction: column;
  }

  .address-actions {
    flex-wrap: wrap;
    gap: 12px;
  }

  .address-text,
  .address-text--wide {
    min-width: 0;
    width: 100%;
  }
}
</style>

